import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { ButtonLauncher } from '../components/ButtonLauncher';
import { ChatWidget } from '../components/ChatWidget';
import { sdkBridge, type InitPayload } from '../sdk-bridge';

// Define search params schema
const searchSchema = z.object({
  organizationId: z.string().min(1),
  assistantId: z.string().min(1),
  greeting: z.string().optional(),
  mode: z.enum(['button', 'chat']).catch('chat')
});

export const Route = createFileRoute('/')({
  validateSearch: searchSchema,
  component: Index
});

function Index() {
  const searchParams = Route.useSearch();
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (searchParams.organizationId && searchParams.assistantId) {
      // If URL params exist, we're ready
      setIsReady(true);

      // Send ready message to parent if embedded
      if (sdkBridge.isEmbedded()) {
        sdkBridge.sendReady();
      }
    } else if (sdkBridge.isEmbedded()) {
      // If running in iframe, wait for SDK initialization
      const handleInit = (payload: unknown) => {
        const initData = payload as InitPayload;

        // Update search params with SDK data
        navigate({
          to: '/',
          search: {
            organizationId: initData.organizationId,
            assistantId: initData.assistantId,
            greeting: initData.greeting,
            mode: searchParams.mode
          },
          replace: true
        });

        setIsReady(true);
      };

      sdkBridge.on('STRADA_INIT', handleInit);

      // Send ready message to request initialization
      sdkBridge.sendReady();

      return () => {
        sdkBridge.off('STRADA_INIT', handleInit);
      };
    } else {
      // Standalone mode without params
      setIsReady(true);
    }
  }, [
    searchParams.organizationId,
    searchParams.assistantId,
    searchParams.greeting,
    searchParams.mode,
    navigate
  ]);

  // Show loading state while waiting for initialization
  if (!isReady) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4127D4] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Strada Widget...</p>
        </div>
      </div>
    );
  }

  // Show error if required params are missing
  if (!searchParams.organizationId || !searchParams.assistantId) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Strada Widget</h1>
          <p className="text-red-600 mb-4">Missing required parameters</p>
          <div className="bg-gray-100 rounded p-4 text-left text-sm">
            <p className="font-semibold mb-2">Required URL Parameters:</p>
            <p className="text-gray-700">
              <span className="font-medium">organizationId:</span>{' '}
              {searchParams.organizationId || '❌ Missing'}
            </p>
            <p className="text-gray-700">
              <span className="font-medium">assistantId:</span> {searchParams.assistantId || '❌ Missing'}
            </p>
          </div>
          <p className="text-xs text-gray-500 mt-6">
            Example: ?organizationId=123&assistantId=456
            <br />
            Or embed using the Strada SDK
          </p>
        </div>
      </div>
    );
  }

  // Render based on mode
  if (searchParams.mode === 'button') {
    return (
      <ButtonLauncher organizationId={searchParams.organizationId} assistantId={searchParams.assistantId} />
    );
  }

  return (
    <ChatWidget
      organizationId={searchParams.organizationId}
      assistantId={searchParams.assistantId}
      greeting={searchParams.greeting}
    />
  );
}
